<!--
  勤怠テーブル（新クロージング・インライン版）の「開始/終了」セル用の時刻入力。

  - 入力・表示は `HH:mm` のみ。type="text" の手入力（年月日なし）。
    タブレットでも OS のネイティブ時刻ピッカーを出さないため、
    type="time" は使わず type="text" の手入力とする。
  - 保存データ（canonical）は既存処理が期待する `YYYY-MM-DDTHH:mm` 形式。
    日付部分は営業日（businessDate prop, YYYY-MM-DD）で組み立てる。
    日付の確定（深夜跨ぎ店舗の翌日補正など）は親側の useTimecardBusinessDate
    （resolveStartAt / resolveLeaveAt）に任せ、本コンポーネントは営業日の日付で
    固定するだけにする。

  v-model（value）は親が持つ canonical な `YYYY-MM-DDTHH:mm` 文字列。
  blur で確定し、有効な HH:mm なら `input`（canonical）と `commit` を emit する。
-->
<template>
  <v-text-field
    v-model="localText"
    type="text"
    placeholder="HH:MM"
    maxlength="5"
    dense
    hide-details
    :clearable="clearable"
    :error="error"
    @focus="$emit('focus')"
    @blur="onBlur"
    @click:clear="onClear"
  />
</template>

<script>
import { ref, watch } from '@vue/composition-api'

// canonical な日時文字列（YYYY-MM-DDTHH:mm[:ss]）から HH:mm を取り出す。
const toTime = value => {
  if (!value) return ''
  const m = String(value).match(/[T ](\d{2}):(\d{2})/)

  return m ? `${m[1]}:${m[2]}` : ''
}

// 時刻文字列をパースする。`HH:mm`（コロンあり）と、コロンなしの数字
// 3〜4 桁（例: 0300→03:00 / 1200→12:00 / 1222→12:22。末尾 2 桁が分、
// 残りが時）に対応する。時 0-23・分 0-59 を満たせば 2 桁ゼロ詰めした
// { hh, mm } を返し、不正なら null を返す。
const parseTime = text => {
  const trimmed = String(text || '').trim()
  let hh
  let mm

  const colonMatch = trimmed.match(/^(\d{1,2}):(\d{1,2})$/)
  const digitsMatch = trimmed.match(/^(\d{3,4})$/)
  if (colonMatch) {
    hh = Number(colonMatch[1])
    mm = Number(colonMatch[2])
  } else if (digitsMatch) {
    const digits = digitsMatch[1]
    hh = Number(digits.slice(0, -2))
    mm = Number(digits.slice(-2))
  } else {
    return null
  }

  if (Number.isNaN(hh) || Number.isNaN(mm)) return null
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null

  return { hh: String(hh).padStart(2, '0'), mm: String(mm).padStart(2, '0') }
}

// 営業日（YYYY-MM-DD）と時刻（HH:mm）から canonical な YYYY-MM-DDTHH:mm を作る。
const buildCanonical = (businessDate, time) => {
  const dateMatch = String(businessDate || '').match(/^(\d{4}-\d{2}-\d{2})$/)
  if (!dateMatch) return null

  return `${dateMatch[1]}T${time}`
}

export default {
  name: 'TimecardDateTimeCell',
  props: {
    // 親が持つ canonical な日時文字列（YYYY-MM-DDTHH:mm）。空は未入力。
    value: {
      type: String,
      default: null,
    },
    // 営業日（YYYY-MM-DD）。canonical の日付部分の組み立てに使う。
    businessDate: {
      type: String,
      default: '',
    },
    error: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    // input に表示する HH:mm 文字列（手入力中の値）。
    const localText = ref(toTime(props.value))

    // 親の canonical 値が外部で変わったら表示へ同期する。
    watch(() => props.value, v => {
      localText.value = toTime(v)
    })

    // blur で確定。HH:mm として有効なら canonical を emit、無効なら元に戻す。
    const onBlur = () => {
      const raw = localText.value

      // 空入力
      if (raw == null || String(raw).trim() === '') {
        if (props.clearable && props.value) {
          emit('input', null)
          emit('commit')
        } else {
          // 必須セルの空入力は元の値に戻す
          localText.value = toTime(props.value)
        }

        return
      }

      const parsed = parseTime(raw)
      if (!parsed) {
        // 不正な入力は元の値に戻す
        localText.value = toTime(props.value)

        return
      }

      // 正規化した表示（HH:mm の 2 桁）に揃える
      const time = `${parsed.hh}:${parsed.mm}`
      localText.value = time

      const canonical = buildCanonical(props.businessDate, time)
      if (canonical && canonical !== props.value) {
        emit('input', canonical)
      }
      emit('commit')
    }

    // clearable の × クリックで空にする。
    const onClear = () => {
      localText.value = ''
      if (props.value) {
        emit('input', null)
        emit('commit')
      }
    }

    return {
      localText,
      onBlur,
      onClear,
    }
  },
}
</script>
